/**
 * React Hooks for @webdevarif/notifications
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import { NotificationClientSDK, ClientConfig } from './sdk';
import { NotificationPayload, NotificationFilters, NotificationCount } from '../types';

export interface UseNotificationsOptions extends NotificationFilters {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export interface UseNotificationsReturn {
  notifications: NotificationPayload[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  markAsRead: (id: string) => Promise<boolean>;
  markAsUnread: (id: string) => Promise<boolean>;
  markAllAsRead: (category?: string) => Promise<{ success: boolean; updatedCount?: number; error?: string }>;
  delete: (id: string) => Promise<boolean>;
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<NotificationPayload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = new NotificationClientSDK({
    baseUrl: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api',
    websocketUrl: process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3000',
    autoConnect: true,
  });

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await client.getNotifications(options);
      if (result.success) {
        setNotifications(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch notifications');
      }
    } catch (err) {
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [client, options]);

  const markAsRead = useCallback(async (id: string): Promise<boolean> => {
    try {
      const result = await client.markAsRead(id);
      if (result.success) {
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, read: true } : n)
        );
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [client]);

  const markAsUnread = useCallback(async (id: string): Promise<boolean> => {
    try {
      const result = await client.markAsUnread(id);
      if (result.success) {
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, read: false } : n)
        );
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [client]);

  const markAllAsRead = useCallback(async (category?: string): Promise<{ success: boolean; updatedCount?: number; error?: string }> => {
    try {
      const result = await client.markAllAsRead(undefined, category);
      if (result.success) {
        setNotifications(prev => 
          prev.map(n => ({ ...n, read: true }))
        );
      }
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to mark all as read' };
    }
  }, [client]);

  const deleteNotification = useCallback(async (id: string): Promise<boolean> => {
    try {
      const result = await client.deleteNotification(id);
      if (result.success) {
        setNotifications(prev => prev.filter(n => n.id !== id));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [client]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Auto-refresh
  useEffect(() => {
    if (!options.autoRefresh) return;

    const interval = setInterval(fetchNotifications, options.refreshInterval || 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, options.autoRefresh, options.refreshInterval]);

  // WebSocket listener for real-time updates
  useEffect(() => {
    const handleNotificationCreated = (event: CustomEvent) => {
      const notification = event.detail;
      setNotifications(prev => [notification, ...prev]);
    };

    window.addEventListener('notification:created', handleNotificationCreated as EventListener);
    return () => {
      window.removeEventListener('notification:created', handleNotificationCreated as EventListener);
    };
  }, []);

  return {
    notifications,
    loading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    delete: deleteNotification,
  };
}

export function useNotificationStats() {
  const [stats, setStats] = useState<NotificationCount | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = new NotificationClientSDK({
    baseUrl: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api',
    websocketUrl: process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3000',
    autoConnect: true,
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await client.getNotificationStats();
      if (result.success) {
        setStats(result.data || null);
      } else {
        setError(result.error || 'Failed to fetch notification stats');
      }
    } catch (err) {
      setError('Failed to fetch notification stats');
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, fetchStats };
}
